const PawblefyTheme = {
  formatMoney(cents) {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return (window.Pawblefy?.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(value))));
  },
  escapeHTML(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character]));
  },
  async getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  },
  async refreshCart(open = false) {
    const cart = await this.getCart();
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    const total = document.querySelector('[data-cart-total]');
    if (total) total.textContent = this.formatMoney(cart.total_price);
    const container = document.querySelector('[data-cart-items]');
    if (container) {
      container.innerHTML = cart.items.length ? cart.items.map((item) => `
        <div class="drawer-item">
          ${item.image ? `<img src="${this.escapeHTML(item.image)}" alt="">` : ''}
          <div><h3>${this.escapeHTML(item.product_title)}</h3><p>${this.escapeHTML(item.variant_title || '')}</p>${Object.entries(item.properties || {}).filter(([, value]) => value).map(([key, value]) => `<p>${this.escapeHTML(key)}: ${this.escapeHTML(value)}</p>`).join('')}<p>${item.quantity} × ${this.formatMoney(item.final_price)}</p></div>
        </div>`).join('') : '<p>Your bag is ready for something cute.</p>';
    }
    if (open) this.openCart();
  },
  openCart() {
    document.body.classList.add('cart-open');
    document.querySelector('[data-cart-drawer]')?.setAttribute('aria-hidden', 'false');
  },
  closeCart() {
    document.body.classList.remove('cart-open');
    document.querySelector('[data-cart-drawer]')?.setAttribute('aria-hidden', 'true');
  },
  initHeader() {
    document.querySelector('[data-menu-toggle]')?.addEventListener('click', (event) => {
      const menu = document.querySelector('[data-main-nav]');
      const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
      event.currentTarget.setAttribute('aria-expanded', String(!expanded));
      menu?.classList.toggle('open', !expanded);
    });
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', () => this.openCart()));
    document.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.closeCart()));
  },
  initGallery(root) {
    const track = root.querySelector('[data-gallery-track]');
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const current = root.querySelector('[data-gallery-current]');
    const previous = root.querySelector('[data-gallery-previous]');
    const next = root.querySelector('[data-gallery-next]');
    if (!track || !thumbs.length) return;
    let activeIndex = 0;

    const selectImage = (index, scrollTrack = true, behavior = 'smooth') => {
      const safeIndex = Math.max(0, Math.min(index, thumbs.length - 1));
      activeIndex = safeIndex;
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === safeIndex;
        thumb.classList.toggle('active', selected);
        thumb.setAttribute('aria-current', String(selected));
      });
      if (current) current.textContent = String(safeIndex + 1);
      if (scrollTrack) track.scrollTo({ left: safeIndex * track.clientWidth, behavior });
      thumbs[safeIndex]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    };

    thumbs.forEach((button, index) => button.addEventListener('click', () => selectImage(index)));
    previous?.addEventListener('click', () => selectImage((activeIndex - 1 + thumbs.length) % thumbs.length));
    next?.addEventListener('click', () => selectImage((activeIndex + 1) % thumbs.length));
    let galleryFrame;
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(galleryFrame);
      galleryFrame = window.requestAnimationFrame(() => {
        if (!track.clientWidth) return;
        selectImage(Math.round(track.scrollLeft / track.clientWidth), false);
      });
    }, { passive: true });
  },
  initProduct(root) {
    const variantsNode = root.querySelector('[data-variants-json]');
    const variantSelect = root.querySelector('[name="id"]');
    const stack = root.querySelector('[data-set-stack]');
    const template = root.querySelector('[data-set-template]');
    if (!variantsNode || !variantSelect || !stack || !template) return;

    const variants = JSON.parse(variantsNode.textContent);
    const leashVariantsNode = root.querySelector('[data-leash-variants-json]');
    const leashVariants = leashVariantsNode ? JSON.parse(leashVariantsNode.textContent) : [];
    const cards = () => Array.from(stack.querySelectorAll('[data-set-card]'));
    const errorBox = root.querySelector('[data-product-error]');

    const showError = (message = '') => {
      if (!errorBox) return;
      errorBox.hidden = !message;
      errorBox.textContent = message;
    };

    const selectedButtons = (card) => Array.from(card.querySelectorAll('[data-set-option-button][aria-pressed="true"]'));
    const selectedVariant = (card) => {
      const picks = {};
      selectedButtons(card).forEach((button) => { picks[button.dataset.position] = button.dataset.value; });
      return variants.find((variant) => variant.options.every((value, index) => !card.querySelector('[data-set-option-button]') || picks[String(index + 1)] === value)) || variants[0];
    };

    const selectedColor = (card) => {
      const picker = Array.from(card.querySelectorAll('[data-set-variant-picker]')).find((node) => String(node.dataset.optionName).toLowerCase().includes('color'));
      const button = picker?.querySelector('[data-set-option-button][aria-pressed="true"]');
      return { name: button?.dataset.value || 'Black', hex: button?.dataset.colorHex || '#22252b' };
    };

    const activateCard = (target, scroll = false) => {
      cards().forEach((card) => {
        const active = card === target;
        card.classList.toggle('active', active);
        card.classList.toggle('collapsed', !active);
        card.querySelector('[data-set-summary]')?.setAttribute('aria-expanded', String(active));
        const fields = card.querySelector('[data-set-fields]');
        if (fields) fields.hidden = !active;
        const editLabel = card.querySelector('[data-set-edit-label]');
        if (editLabel) editLabel.textContent = active ? 'Editing' : 'Edit';
        card.querySelectorAll('[data-set-name], [data-set-phone]').forEach((input) => { input.disabled = !active; });
      });
      if (scroll) window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
    };

    const syncLeash = (card, color) => {
      const addon = card.querySelector('[data-set-leash-addon]');
      const toggle = card.querySelector('[data-set-leash-toggle]');
      if (!addon || !toggle || !leashVariants.length) return null;
      const exact = leashVariants.find((variant) => variant.available && variant.title.toLowerCase() === color.name.toLowerCase());
      const fallback = leashVariants.find((variant) => variant.available && variant.title.toLowerCase() === 'black') || leashVariants.find((variant) => variant.available);
      const leashVariant = exact || fallback;
      const label = card.querySelector('[data-set-leash-label]');
      if (!leashVariant) {
        toggle.checked = false;
        toggle.disabled = true;
        if (label) label.textContent = 'Matching leash is currently unavailable.';
        return null;
      }
      toggle.disabled = false;
      addon.dataset.variantId = leashVariant.id;
      addon.dataset.colorName = leashVariant.title;
      addon.dataset.price = leashVariant.price;
      addon.classList.toggle('active', toggle.checked);
      if (label) label.textContent = `We’ll add the ${leashVariant.title.toLowerCase()} matching leash.`;
      return leashVariant;
    };

    const syncCard = (card) => {
      const variant = selectedVariant(card);
      const color = selectedColor(card);
      card.dataset.variantId = variant.id;
      card.dataset.variantPrice = variant.price;
      card.dataset.variantAvailable = String(variant.available);
      card.dataset.colorName = color.name;

      card.querySelectorAll('[data-set-variant-picker]').forEach((picker) => {
        const selected = picker.querySelector('[data-set-option-button][aria-pressed="true"]');
        const label = picker.querySelector('[data-set-option-label]');
        if (selected && label) label.textContent = selected.dataset.value;
      });

      const preview = card.querySelector('[data-set-preview]');
      if (preview) preview.style.backgroundColor = color.hex;
      const colorPreviewLabel = card.querySelector('[data-set-color-preview-label]');
      if (colorPreviewLabel) colorPreviewLabel.textContent = `${color.name} collar preview`;

      const name = card.querySelector('[data-set-name]')?.value.trim();
      const number = Number(card.dataset.setIndex || 1);
      const nameSummary = card.querySelector('[data-set-name-summary]');
      const optionsSummary = card.querySelector('[data-set-options-summary]');
      if (nameSummary) nameSummary.textContent = name || `Personalized set ${number}`;
      if (optionsSummary) optionsSummary.textContent = selectedButtons(card).map((button) => button.dataset.value).join(' · ') || variant.title;
      syncLeash(card, color);
      return variant;
    };

    const updateIndexes = () => {
      const allCards = cards();
      allCards.forEach((card, index) => {
        const number = index + 1;
        card.dataset.setIndex = number;
        const numberLabel = card.querySelector('[data-set-number]');
        const heading = card.querySelector('[data-set-heading-number]');
        const discount = card.querySelector('[data-set-discount]');
        const remove = card.querySelector('[data-remove-set]');
        const addAnother = card.querySelector('[data-add-another-set]');
        if (numberLabel) numberLabel.textContent = number;
        if (heading) heading.textContent = `Set ${number}`;
        if (discount) {
          discount.hidden = number === 1;
          discount.textContent = number === 2 ? '10% tier' : number === 3 ? '20% tier' : '';
        }
        if (remove) remove.hidden = number === 1;
        if (addAnother) {
          addAnother.hidden = number !== allCards.length || allCards.length >= 3;
          if (addAnother.firstChild) addAnother.firstChild.nodeValue = `Save Set ${number} & add another `;
          const tier = addAnother.querySelector('[data-next-tier-label]');
          if (tier) tier.textContent = allCards.length === 1 ? 'Unlock 10% off →' : 'Unlock 20% off →';
        }
      });
    };

    const updateTotals = () => {
      const allCards = cards();
      const cardVariants = allCards.map((card) => syncCard(card));
      const productSubtotal = cardVariants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
      const leashTotal = allCards.reduce((sum, card) => {
        const toggle = card.querySelector('[data-set-leash-toggle]');
        const addon = card.querySelector('[data-set-leash-addon]');
        return sum + (toggle?.checked ? Number(addon?.dataset.price || 0) : 0);
      }, 0);
      const discountRate = allCards.length >= 3 ? 0.2 : allCards.length === 2 ? 0.1 : 0;
      const savings = Math.round(productSubtotal * discountRate);
      const total = productSubtotal - savings + leashTotal;
      const activeCard = allCards.find((card) => card.classList.contains('active')) || allCards[0];
      const activeVariant = selectedVariant(activeCard);

      variantSelect.value = activeVariant.id;
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      if (price) price.textContent = this.formatMoney(activeVariant.price);
      if (compare) {
        compare.textContent = activeVariant.compare_at_price ? this.formatMoney(activeVariant.compare_at_price) : '';
        compare.hidden = !activeVariant.compare_at_price;
      }

      root.querySelectorAll('[data-bundle-step]').forEach((step) => {
        const stepNumber = Number(step.dataset.bundleStep);
        step.classList.toggle('active', stepNumber === allCards.length || (stepNumber === 3 && allCards.length >= 3));
        step.classList.toggle('complete', stepNumber < allCards.length);
      });
      const countLabel = root.querySelector('[data-bundle-count]');
      const leashLabel = root.querySelector('[data-bundle-leash-count]');
      const savingsLabel = root.querySelector('[data-bundle-savings]');
      const totalLabel = root.querySelector('[data-bundle-total]');
      const checkedLeashes = allCards.filter((card) => card.querySelector('[data-set-leash-toggle]')?.checked).length;
      if (countLabel) countLabel.textContent = `${allCards.length} personalized ${allCards.length === 1 ? 'set' : 'sets'}`;
      if (leashLabel) {
        leashLabel.hidden = checkedLeashes === 0;
        leashLabel.textContent = checkedLeashes ? `Includes ${checkedLeashes} matching ${checkedLeashes === 1 ? 'leash' : 'leashes'}` : '';
      }
      if (savingsLabel) {
        savingsLabel.hidden = savings === 0;
        savingsLabel.textContent = savings ? `You save ${this.formatMoney(savings)}` : '';
      }
      if (totalLabel) totalLabel.textContent = this.formatMoney(total);

      const submit = root.querySelector('[data-product-submit]');
      const allAvailable = cardVariants.every((variant) => variant.available);
      if (submit) {
        submit.disabled = !allAvailable;
        submit.textContent = allAvailable ? `Add ${allCards.length === 1 ? 'set' : `${allCards.length} sets`} to bag · ${this.formatMoney(total)}` : 'One option is sold out';
      }
      const note = root.querySelector('[data-automatic-discount-note]');
      if (note) {
        note.hidden = !discountRate;
        note.textContent = discountRate ? `Your ${Math.round(discountRate * 100)}% multi-set discount applies automatically in cart and checkout.` : '';
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', activeVariant.id);
      window.history.replaceState({}, '', url);
      updateIndexes();
    };

    const validateCard = (card) => {
      const name = card.querySelector('[data-set-name]');
      const phone = card.querySelector('[data-set-phone]');
      if (!name?.value.trim() || !phone?.value.trim()) {
        activateCard(card);
        showError('Add your pet’s name and phone number before continuing.');
        if (!name?.value.trim()) name?.reportValidity(); else phone?.reportValidity();
        return false;
      }
      showError();
      return true;
    };

    root.addEventListener('click', (event) => {
      const summary = event.target.closest('[data-set-summary]');
      if (summary) {
        activateCard(summary.closest('[data-set-card]'));
        updateTotals();
        return;
      }
      const option = event.target.closest('[data-set-option-button]');
      if (option) {
        const picker = option.closest('[data-set-variant-picker]');
        picker.querySelectorAll('[data-set-option-button]').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
        updateTotals();
        return;
      }
      const font = event.target.closest('[data-set-font-option]');
      if (font) {
        const card = font.closest('[data-set-card]');
        card.querySelectorAll('[data-set-font-option]').forEach((button) => button.setAttribute('aria-pressed', String(button === font)));
        card.querySelector('[data-set-font-input]').value = font.dataset.fontName;
        card.querySelector('[data-set-font-label]').textContent = font.dataset.fontName;
        card.querySelector('[data-set-preview]').style.fontFamily = `"${font.dataset.fontFamily}", sans-serif`;
        return;
      }
      const fontColor = event.target.closest('[data-set-font-color-option]');
      if (fontColor) {
        const card = fontColor.closest('[data-set-card]');
        card.querySelectorAll('[data-set-font-color-option]').forEach((button) => button.setAttribute('aria-pressed', String(button === fontColor)));
        card.querySelector('[data-set-font-color-input]').value = fontColor.dataset.colorName;
        card.querySelector('[data-set-font-color-label]').textContent = fontColor.dataset.colorName;
        card.querySelector('[data-set-preview]').style.color = fontColor.dataset.colorValue;
        return;
      }
      const addAnother = event.target.closest('[data-add-another-set]');
      if (addAnother) {
        const current = addAnother.closest('[data-set-card]');
        if (!validateCard(current) || cards().length >= 3) return;
        const fragment = template.content.cloneNode(true);
        const nextCard = fragment.querySelector('[data-set-card]');
        stack.appendChild(fragment);
        updateIndexes();
        activateCard(nextCard, true);
        updateTotals();
        return;
      }
      const remove = event.target.closest('[data-remove-set]');
      if (remove) {
        const card = remove.closest('[data-set-card]');
        const allCards = cards();
        const index = allCards.indexOf(card);
        card.remove();
        const remaining = cards();
        updateIndexes();
        activateCard(remaining[Math.max(0, index - 1)] || remaining[0]);
        updateTotals();
      }
    });

    root.addEventListener('input', (event) => {
      const card = event.target.closest('[data-set-card]');
      if (!card) return;
      if (event.target.matches('[data-set-name]')) {
        card.querySelector('[data-set-name-preview]').textContent = event.target.value || 'YOUR PUP';
      }
      if (event.target.matches('[data-set-phone]')) {
        card.querySelector('[data-set-phone-preview]').textContent = event.target.value || 'YOUR NUMBER';
      }
      showError();
      syncCard(card);
    });

    root.addEventListener('change', (event) => {
      if (!event.target.matches('[data-set-leash-toggle]')) return;
      event.target.closest('[data-set-leash-addon]')?.classList.toggle('active', event.target.checked);
      updateTotals();
    });

    updateIndexes();
    activateCard(cards()[0]);
    updateTotals();
    this.initGallery(root);
  },
  initForms() {
    document.querySelectorAll('[data-product-root]').forEach((root) => this.initProduct(root));
    document.querySelectorAll('form.product-form').forEach((form) => form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const root = form.closest('[data-product-root]');
      const cards = Array.from(form.querySelectorAll('[data-set-card]'));
      const errorBox = form.querySelector('[data-product-error]');

      for (const card of cards) {
        const name = card.querySelector('[data-set-name]');
        const phone = card.querySelector('[data-set-phone]');
        if (!name?.value.trim() || !phone?.value.trim()) {
          card.querySelector('[data-set-summary]')?.click();
          if (errorBox) { errorBox.hidden = false; errorBox.textContent = 'Finish every pet’s name and phone number before adding the sets to your bag.'; }
          window.setTimeout(() => { if (!name?.value.trim()) name?.reportValidity(); else phone?.reportValidity(); }, 20);
          return;
        }
      }

      const button = form.querySelector('[type="submit"]');
      const original = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Adding every set…'; }
      try {
        const tier = cards.length >= 3 ? '20% automatic discount' : cards.length === 2 ? '10% automatic discount' : 'Single set';
        const items = [];
        cards.forEach((card, index) => {
          const properties = {
            'Pet name': card.querySelector('[data-set-name]').value.trim(),
            'Phone number': card.querySelector('[data-set-phone]').value.trim(),
            'Font': card.querySelector('[data-set-font-input]').value,
            'Font color': card.querySelector('[data-set-font-color-input]').value,
            'Set number': `${index + 1} of ${cards.length}`,
            'Multi-set tier': tier
          };
          card.querySelectorAll('[data-set-option-button][aria-pressed="true"]').forEach((option) => { properties[option.dataset.optionName] = option.dataset.value; });
          items.push({ id: Number(card.dataset.variantId), quantity: 1, properties });

          const leashAddon = card.querySelector('[data-set-leash-addon]');
          const leashToggle = card.querySelector('[data-set-leash-toggle]');
          if (leashAddon && leashToggle?.checked && leashAddon.dataset.variantId) {
            items.push({
              id: Number(leashAddon.dataset.variantId),
              quantity: 1,
              properties: {
                'Matching color': leashAddon.dataset.colorName,
                'Add-on for': leashAddon.dataset.sourceProduct,
                'Set number': `${index + 1} of ${cards.length}`
              }
            });
          }
        });

        const response = await fetch(window.Pawblefy.routes.add, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || 'Unable to add these personalized sets.');
        await this.refreshCart(true);
        if (errorBox) errorBox.hidden = true;
        if (button) button.textContent = 'All sets added! ✓';
      } catch (error) {
        if (errorBox) { errorBox.hidden = false; errorBox.textContent = error.message; }
      } finally {
        window.setTimeout(() => { if (button) { button.disabled = false; button.textContent = original; } }, 1800);
      }
    }));
  },
  init() { this.initHeader(); this.initForms(); this.refreshCart(false).catch(() => {}); }
};

document.addEventListener('DOMContentLoaded', () => PawblefyTheme.init());
